import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { MessageCircle, Clock, CheckCircle, AlertCircle, Trash2, X, Bug, Lightbulb, Zap, Image as ImageIcon, Search } from 'lucide-react';

const AdminFeedbackList = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'feedbacks'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const feedbackData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFeedbacks(feedbackData);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      await updateDoc(doc(db, 'feedbacks', feedbackId), { status: newStatus, isRead: true });
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback(prev => ({ ...prev, status: newStatus, isRead: true }));
      }
    } catch (error) {
      console.error('상태 업데이트 실패:', error);
      alert('상태 변경에 실패했습니다.');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'feedbacks', feedbackId));
      setSelectedFeedback(null);
    } catch (error) {
      console.error('삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const filteredFeedbacks = feedbacks.filter(fb => {
    const matchesFilter = filter === 'all' || fb.status === filter;
    const matchesSearch = !searchQuery || 
      fb.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fb.authorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fb.content?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getTypeConfig = (type) => {
    const configs = {
      bug: { icon: <Bug size={16} />, label: '버그', bg: '#fee2e2', color: '#dc2626' },
      suggestion: { icon: <Lightbulb size={16} />, label: '제안', bg: '#fef3c7', color: '#d97706' },
      improvement: { icon: <Zap size={16} />, label: '개선', bg: '#dbeafe', color: '#2563eb' }
    };
    return configs[type] || { icon: <MessageCircle size={16} />, label: type, bg: '#f3f4f6', color: '#6b7280' };
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { icon: <Clock size={14} />, label: '대기', bg: '#fef3c7', color: '#92400e' },
      processing: { icon: <AlertCircle size={14} />, label: '처리중', bg: '#dbeafe', color: '#1e40af' },
      resolved: { icon: <CheckCircle size={14} />, label: '완료', bg: '#d1fae5', color: '#065f46' }
    };
    return configs[status] || configs.pending;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('ko-KR') + ' ' + date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  };

  const stats = {
    total: feedbacks.length,
    pending: feedbacks.filter(f => f.status === 'pending').length,
    processing: feedbacks.filter(f => f.status === 'processing').length,
    resolved: feedbacks.filter(f => f.status === 'resolved').length
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>로딩 중...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}><MessageCircle size={32} color="#6366f1" />피드백 관리</h1>
          <p style={styles.subtitle}>사용자 피드백을 확인하고 처리하세요</p>
        </div>
      </div>

      {/* 통계 카드 */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#eef2ff', color: '#6366f1' }}><MessageCircle size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.total}</div><div style={styles.statLabel}>전체</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7', color: '#f59e0b' }}><Clock size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.pending}</div><div style={styles.statLabel}>대기</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#dbeafe', color: '#3b82f6' }}><AlertCircle size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.processing}</div><div style={styles.statLabel}>처리중</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#d1fae5', color: '#10b981' }}><CheckCircle size={24} /></div>
          <div style={styles.statContent}><div style={styles.statValue}>{stats.resolved}</div><div style={styles.statLabel}>완료</div></div>
        </div>
      </div>

      {/* 필터 & 검색 */}
      <div style={styles.controls}>
        <div style={styles.filterButtons}>
          {[
            { key: 'all', label: '전체' },
            { key: 'pending', label: '대기' },
            { key: 'processing', label: '처리중' },
            { key: 'resolved', label: '완료' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={filter === f.key ? { ...styles.filterButton, ...styles.filterButtonActive } : styles.filterButton}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={styles.searchBox}>
          <Search size={20} color="#94a3b8" />
          <input
            type="text"
            placeholder="제목, 작성자, 내용 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      {/* 레이아웃 */}
      <div style={styles.layout}>
        {/* 피드백 목록 */}
        <div style={styles.listContainer}>
          {filteredFeedbacks.length === 0 ? (
            <div style={styles.emptyState}>
              <MessageCircle size={48} color="#cbd5e1" />
              <p style={styles.emptyText}>피드백이 없습니다</p>
            </div>
          ) : (
            filteredFeedbacks.map(feedback => {
              const typeConfig = getTypeConfig(feedback.type);
              const statusConfig = getStatusConfig(feedback.status);
              return (
                <div
                  key={feedback.id}
                  onClick={() => setSelectedFeedback(feedback)}
                  style={{
                    ...styles.feedbackItem,
                    ...(selectedFeedback?.id === feedback.id ? styles.feedbackItemSelected : {}),
                    ...(!feedback.isRead ? styles.feedbackItemUnread : {})
                  }}
                >
                  <div style={styles.feedbackItemHeader}>
                    <div style={{ ...styles.typeBadge, backgroundColor: typeConfig.bg, color: typeConfig.color }}>
                      {typeConfig.icon}
                      <span>{typeConfig.label}</span>
                    </div>
                    <div style={{ ...styles.statusBadge, backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                      {statusConfig.icon}
                      <span>{statusConfig.label}</span>
                    </div>
                  </div>
                  <h4 style={styles.feedbackTitle}>
                    {!feedback.isRead && <span style={styles.newBadge}>N</span>}
                    {feedback.title}
                  </h4>
                  <p style={styles.feedbackMeta}>{feedback.authorName} · {formatDate(feedback.createdAt)}</p>
                </div>
              );
            })
          )}
        </div>

        {/* 상세 보기 */}
        {selectedFeedback && (
          <div style={styles.detailContainer}>
            <div style={styles.detailHeader}>
              <div>
                <h3 style={styles.detailTitle}>{selectedFeedback.title}</h3>
                <div style={styles.detailMeta}>
                  <span style={{ ...styles.typeBadge, ...getTypeConfig(selectedFeedback.type), backgroundColor: getTypeConfig(selectedFeedback.type).bg, color: getTypeConfig(selectedFeedback.type).color }}>
                    {getTypeConfig(selectedFeedback.type).icon}
                    <span>{getTypeConfig(selectedFeedback.type).label}</span>
                  </span>
                  <span style={styles.metaText}>{selectedFeedback.authorName}</span>
                  <span style={styles.metaText}>{selectedFeedback.authorEmail}</span>
                  <span style={styles.metaText}>{formatDate(selectedFeedback.createdAt)}</span>
                </div>
              </div>
              <button onClick={() => setSelectedFeedback(null)} style={styles.closeButton}><X size={20} /></button>
            </div>

            <div style={styles.detailContent}>
              <pre style={styles.contentText}>{selectedFeedback.content}</pre>
            </div>

            {selectedFeedback.images && selectedFeedback.images.length > 0 && (
              <div style={styles.imagesSection}>
                <h4 style={styles.imagesSectionTitle}><ImageIcon size={18} /> 첨부 이미지 ({selectedFeedback.images.length})</h4>
                <div style={styles.imageGrid}>
                  {selectedFeedback.images.map((url, index) => (
                    <a key={index} href={url} target="_blank" rel="noopener noreferrer" style={styles.imageLink}>
                      <img src={url} alt={`attachment-${index}`} style={styles.image} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.detailActions}>
              <div style={styles.statusSelectWrapper}>
                <label style={styles.selectLabel}>상태 변경</label>
                <select
                  value={selectedFeedback.status}
                  onChange={(e) => updateStatus(selectedFeedback.id, e.target.value)}
                  style={styles.statusSelect}
                >
                  <option value="pending">대기</option>
                  <option value="processing">처리중</option>
                  <option value="resolved">완료</option>
                </select>
              </div>
              <button onClick={() => deleteFeedback(selectedFeedback.id)} style={styles.deleteButton}>
                <Trash2 size={18} />삭제
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '24px' },
  header: { marginBottom: '24px' },
  title: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: '700', color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '14px', color: '#64748b', margin: '8px 0 0 0' },

  // 통계
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
  statIcon: { width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' },
  statContent: { flex: 1 },
  statValue: { fontSize: '24px', fontWeight: '700', color: '#0f172a' },
  statLabel: { fontSize: '13px', color: '#64748b' },

  // 컨트롤
  controls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' },
  filterButtons: { display: 'flex', gap: '8px' },
  filterButton: { padding: '10px 20px', backgroundColor: '#f8fafc', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
  filterButtonActive: { backgroundColor: '#6366f1', color: 'white', borderColor: '#6366f1' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', minWidth: '300px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px' },

  // 레이아웃
  layout: { display: 'grid', gridTemplateColumns: '400px 1fr', gap: '24px', minHeight: '600px' },

  // 목록
  listContainer: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '12px', maxHeight: '700px', overflowY: 'auto' },
  feedbackItem: { padding: '16px', borderRadius: '10px', marginBottom: '8px', cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent', backgroundColor: '#f8fafc' },
  feedbackItemSelected: { borderColor: '#6366f1', backgroundColor: '#eef2ff' },
  feedbackItemUnread: { backgroundColor: '#fffbeb', borderColor: '#fcd34d' },
  feedbackItemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  typeBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' },
  statusBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' },
  feedbackTitle: { margin: '8px 0', fontSize: '15px', fontWeight: '600', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  newBadge: { display: 'inline-block', padding: '2px 6px', backgroundColor: '#ef4444', color: 'white', fontSize: '10px', fontWeight: '700', borderRadius: '4px', marginRight: '6px' },
  feedbackMeta: { margin: 0, fontSize: '12px', color: '#94a3b8' },

  // 상세
  detailContainer: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', maxHeight: '700px', overflowY: 'auto' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' },
  detailTitle: { margin: '0 0 12px 0', fontSize: '22px', fontWeight: '700', color: '#0f172a' },
  detailMeta: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' },
  metaText: { fontSize: '13px', color: '#64748b' },
  closeButton: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white', color: '#64748b', cursor: 'pointer' },

  detailContent: { marginBottom: '24px' },
  contentText: { whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontFamily: 'inherit', fontSize: '15px', lineHeight: '1.7', color: '#334155', margin: 0, padding: '20px', backgroundColor: '#f8fafc', borderRadius: '10px' },

  imagesSection: { marginBottom: '24px' },
  imagesSectionTitle: { display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' },
  imageGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' },
  imageLink: { display: 'block', borderRadius: '10px', overflow: 'hidden', border: '2px solid #e2e8f0', transition: 'all 0.2s' },
  image: { width: '100%', height: '150px', objectFit: 'cover', display: 'block' },

  detailActions: { display: 'flex', gap: '12px', alignItems: 'flex-end', paddingTop: '20px', borderTop: '1px solid #e2e8f0' },
  statusSelectWrapper: { flex: 1 },
  selectLabel: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' },
  statusSelect: { width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', backgroundColor: 'white', outline: 'none' },
  deleteButton: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

  // 빈 상태
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' },
  emptyText: { fontSize: '16px', fontWeight: '600', color: '#94a3b8', margin: '16px 0 0 0' },

  // 로딩
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px' },
  spinner: { width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};

export default AdminFeedbackList;